import { match, P } from "ts-pattern"
import {
    AvailableSuitesResponse,
    CheckoutStatusResponse,
    FareOptionsResponse,
    PricesResponseV2,
    StartPaymentResponse,
    ValidateCheckoutRequest,
    ValidateCheckoutResponse,
    ValidateQuoteRequest,
    ValidateQuoteResponse,
} from "@ssc-booking/api-contracts"
import * as Occupancy from "@ssc-booking/api-contracts/src/occupancy"
import * as E from "fp-ts/Either"
import { failure } from "io-ts/PathReporter"
import { Request } from "../../support/websocket-server-stub"
import fareOptions from "./fare-options"
import availableSuites from "./available-suites"
import validateCheckout from "./validate-checkout"
import checkoutStatus from "./checkout-status"
import startPaymentResponse from "./start-payment"
import validateQuote from "./validate-quote"
import pricesV2_P2P from "./prices-v2-p2p"
import pricesV2_D2DEconomy from "./prices-v2-d2d"
import pricesV2_D2DBusiness from "./prices-v2-d2d-business"
import pricesV2_Essential from "./prices-v2-ess"

const matchPricesV2 = (request: Request) =>
    match({
        fare: request.data.fareCode,
        airType: request.data.air.type,
        airClass: request.data.air.airClass,
    })
        .with({ fare: "PortToPortV2" }, () => PricesResponseV2.strictType.encode(pricesV2_P2P))
        .with({ fare: "DoorToDoorV2", airType: "flight", airClass: "Economy" }, () =>
            PricesResponseV2.strictType.encode(pricesV2_D2DEconomy),
        )
        .with({ fare: "DoorToDoorV2", airType: "flight", airClass: "Business" }, () =>
            PricesResponseV2.strictType.encode(pricesV2_D2DBusiness),
        )
        .with({ fare: "Essential" }, () => PricesResponseV2.strictType.encode(pricesV2_Essential))
        .otherwise(() => undefined)

const matchValidateCheckout = (request: Request) => {
    const result = ValidateCheckoutRequest.strictType.decode(request.data)
    if (E.isLeft(result)) {
        console.error(`${request.action} request is not valid`, failure(result.left))
        return undefined
    }
    const validatedRequest = result.right

    return match(validatedRequest)
        .with({ price: { occupancy: Occupancy.of(2) }, otherGuests: P.when((x) => x.length === 1) }, () => ({
            action: "validate-checkout",
            type: "ValidateCheckoutResponse",
            data: ValidateCheckoutResponse.strictType.encode(validateCheckout),
        }))
        .otherwise(() => {
            console.error(`${request.action} request data doesn't match any expected values`)
            return undefined
        })
}

const matchValidateQuote = (request: Request) => {
    const result = ValidateQuoteRequest.strictType.decode(request.data)
    if (E.isLeft(result)) {
        console.error(`${request.action} request is not valid`, failure(result.left))
        return undefined
    }
    const validatedRequest = result.right

    return match(validatedRequest)
        .with({ price: { occupancy: Occupancy.of(2) } }, () => ({
            action: "validate-quote",
            type: "ValidateQuoteResponse",
            data: ValidateQuoteResponse.strictType.encode(validateQuote),
        }))
        .otherwise(() => {
            console.error(`${request.action} request data doesn't match any expected values`)
            return undefined
        })
}

export const requestRoutesV4 = (request: Request) =>
    match(request.action)
        .with("fare-options", () => ({
            action: "fare-options",
            type: "FareOptionsResponse",
            data: FareOptionsResponse.strictType.encode(fareOptions),
        }))
        .with("prices-v2", () => ({
            action: "prices-v2",
            type: "PricesResponseV2",
            data: matchPricesV2(request),
        }))
        .with("available-suites", () => ({
            action: "available-suites",
            type: "AvailableSuitesResponse",
            data: AvailableSuitesResponse.strictType.encode(availableSuites),
        }))
        .with("validate-checkout", () => matchValidateCheckout(request))
        .with("checkout-status", () => ({
            action: "checkout-status",
            type: CheckoutStatusResponse.strictType.name,
            data: CheckoutStatusResponse.strictType.encode(checkoutStatus),
        }))
        .with("start-payment", () => ({
            action: "start-payment",
            type: "StartPaymentResponse",
            data: StartPaymentResponse.strictType.encode(startPaymentResponse),
        }))
        .with("validate-quote", () => matchValidateQuote(request))
        .otherwise(() => undefined)
